// src/services/pointsCalculator.js

/**
 * Calculate points based on count and schedule
 * @param {Array} schedule - Points schedule array
 * @param {number} count - Number competing
 * @returns {number} Points (0-5)
 */
const getPoints = (schedule, count) => {
  if (!schedule || count <= 0) return 0;
  
  for (let i = schedule.length - 1; i >= 0; i--) {
    if (count >= schedule[i].count) {
      return Math.min(schedule[i].points, 5);
    }
  }
  return 0;
};

/**
 * Calculate Championship Points (for Class Dogs seeking Champion status)
 * Veterans and non-regular dogs NOT counted
 * @param {Object} params - Calculation parameters
 * @returns {Object} Championship points breakdown
 */
const calculateChampionshipPoints = ({
  dogSchedule,
  bitchSchedule,
  classDogs = 0,
  classBitches = 0,
  championDogs = 0,
  championBitches = 0
}) => {
  const totalChampions = championDogs + championBitches;
  const totalDogs = classDogs + championDogs;
  const totalBitches = classBitches + championBitches;
  
  // Winners Dog/Bitch (class only)
  const WD = getPoints(dogSchedule, classDogs);
  const WB = getPoints(bitchSchedule, classBitches);
  
  // BOB points (class of winner's sex + ALL champions)
  const BOB_dog = getPoints(dogSchedule, classDogs + totalChampions);
  const BOB_bitch = getPoints(bitchSchedule, classBitches + totalChampions);
  
  // BOB points for Champions (all dogs/bitches + ALL champions)
  const BOB_championDog = getPoints(dogSchedule, totalDogs + totalChampions);
  const BOB_championBitch = getPoints(bitchSchedule, totalBitches + totalChampions);
  
  // BOW is simply the higher of WD or WB
  const BOW = Math.max(WD, WB);
  
  // Special BOW rule: minimum 1 point if combined class competition warrants it
  const combinedClassPoints = Math.max(
    getPoints(dogSchedule, classDogs + classBitches), 
    getPoints(bitchSchedule, classDogs + classBitches)
  );
  const finalBOW = (WD === 0 && WB === 0 && combinedClassPoints > 0) ? 1 : BOW;
  
  // BOS calculations - gets points for total same-sex competition
  const BOS_dog = getPoints(dogSchedule, totalDogs);
  const BOS_bitch = getPoints(bitchSchedule, totalBitches);
  
  return {
    WD: {
      points: WD,
      label: "Winners Dog (WD)",
      description: `${classDogs} class dogs competing`
    },
    WB: {
      points: WB,
      label: "Winners Bitch (WB)",
      description: `${classBitches} class bitches competing`
    },
    BOW: {
      points: finalBOW,
      label: "Best of Winners (BOW)",
      description: finalBOW === 1 && WD === 0 && WB === 0 ? 
        `Special rule: 1 pt for combined class` : 
        `Higher of WD(${WD}) or WB(${WB})`
    },
    BOB: {
      dog: {
        points: BOB_dog,
        label: "If WD goes Best of Breed",
        description: `${classDogs} class + ${totalChampions} champions = ${classDogs + totalChampions} total`
      },
      bitch: {
        points: BOB_bitch,
        label: "If WB goes Best of Breed",
        description: `${classBitches} class + ${totalChampions} champions = ${classBitches + totalChampions} total`
      },
      championDog: {
        points: BOB_championDog,
        label: "If Champion Dog goes Best of Breed",
        description: `${totalDogs} dogs + ${totalChampions} champions = ${totalDogs + totalChampions} total`
      },
      championBitch: {
        points: BOB_championBitch,
        label: "If Champion Bitch goes Best of Breed",
        description: `${totalBitches} bitches + ${totalChampions} champions = ${totalBitches + totalChampions} total`
      }
    },
    BOS: {
      dog: {
        points: BOS_dog,
        label: "If Dog goes Best Opposite Sex",
        description: `${totalDogs} total dogs in competition`
      },
      bitch: {
        points: BOS_bitch,
        label: "If Bitch goes Best Opposite Sex",
        description: `${totalBitches} total bitches in competition`
      }
    },
    summary: {
      highestPoints: Math.max(WD, WB, finalBOW, BOB_dog, BOB_bitch, BOB_championDog, BOB_championBitch, BOS_dog, BOS_bitch),
      isMajor: Math.max(WD, WB, finalBOW, BOB_dog, BOB_bitch, BOB_championDog, BOB_championBitch, BOS_dog, BOS_bitch) >= 3
    }
  };
};

/**
 * Calculate Grand Champion Points (for Champion Dogs seeking GCH status)
 * Veterans and non-regular dogs ARE counted (effective 11/1/17)
 * @param {Object} params - Calculation parameters
 * @returns {Object} Grand Champion points breakdown
 */
const calculateGrandChampionPoints = ({
  dogSchedule,
  bitchSchedule,
  classDogs = 0,
  classBitches = 0,
  championDogs = 0,
  championBitches = 0,
  championshipBOS = null // Pass championship BOS calculations
}) => {
  const totalCompeting = classDogs + classBitches + championDogs + championBitches;
  const totalDogs = classDogs + championDogs;
  const totalBitches = classBitches + championBitches;
  
  // BOS points (all of same sex)
  const dogBOS = getPoints(dogSchedule, totalDogs);
  const bitchBOS = getPoints(bitchSchedule, totalBitches);
  
  // BOB calculation - CRITICAL: Use sex-specific schedules for total competition
  // Dogs use dog schedule for total competition
  const dogBOBFromTotal = getPoints(dogSchedule, totalCompeting);
  // Bitches use bitch schedule for total competition
  const bitchBOBFromTotal = getPoints(bitchSchedule, totalCompeting);
  
  // BOB is HIGHER of total competition OR BOS for their sex
  const dogBOB = Math.max(dogBOBFromTotal, dogBOS);
  const bitchBOB = Math.max(bitchBOBFromTotal, bitchBOS);
  
  // Select Dog/Bitch (total minus 1 per AKC rule)
  const SD = totalDogs > 1 ? getPoints(dogSchedule, totalDogs - 1) : 0;
  const SB = totalBitches > 1 ? getPoints(bitchSchedule, totalBitches - 1) : 0;
  
  // GCH points = highest BOB + defeated champions
  const highestBOB = Math.max(dogBOB, bitchBOB);
  const defeatedChampions = championDogs + championBitches - (highestBOB > 0 ? 1 : 0);
  const gchPoints = Math.min(highestBOB + Math.max(0, defeatedChampions), 5);
  
  return {
    BOB: {
      dog: {
        points: dogBOB,
        label: "Champion Dog getting BOB",
        description: `${dogBOB} pts (max of ${dogBOBFromTotal} from ${totalCompeting} total, ${dogBOS} from ${totalDogs} dogs)`
      },
       bitch: {
         points: bitchBOB,
         label: "Champion Bitch getting BOB",
         description: `${bitchBOB} pts (max of ${bitchBOBFromTotal} from ${totalCompeting} total, ${bitchBOS} from ${totalBitches} bitches)`
       }
    },
    BOS: {
      dog: {
        points: dogBOS,
        label: "Champion Dog getting BOS",
        description: `${dogBOS} pts for ${totalDogs} dogs`
      },
      bitch: {
        points: bitchBOS,
        label: "Champion Bitch getting BOS",
        description: `${bitchBOS} pts for ${totalBitches} bitches`
      }
    },
    select: {
      dog: {
        points: SD,
        label: "Select Dog (SD)",
        description: `${SD} pts for ${totalDogs - 1} dogs (${totalDogs} total - 1)`
      },
      bitch: {
        points: SB,
        label: "Select Bitch (SB)",
        description: `${SB} pts for ${totalBitches - 1} bitches (${totalBitches} total - 1)`
      }
    },
    grandChampion: {
      points: gchPoints,
      label: "Grand Champion Points",
      description: `${highestBOB} (BOB) + ${Math.max(0, defeatedChampions)} (defeated champions) = ${gchPoints} (max 5)`,
      defeatedChampions: Math.max(0, defeatedChampions)
    },
    summary: {
      highestBOB,
      gchPoints,
      isMajor: gchPoints >= 3
    }
  };
};

/**
 * Calculate all points for both Championship and Grand Championship
 * @param {Object} params - Calculation parameters
 * @param {Array} params.dogSchedule - Dog points schedule
 * @param {Array} params.bitchSchedule - Bitch points schedule
 * @param {number} params.classDogs - Number of class dogs
 * @param {number} params.classBitches - Number of class bitches
 * @param {number} params.championDogs - Number of champion dogs
 * @param {number} params.championBitches - Number of champion bitches
 * @returns {Object} Complete points breakdown for all awards
 */
export const calculateAllPoints = ({
  dogSchedule,
  bitchSchedule,
  classDogs = 0,
  classBitches = 0,
  championDogs = 0,
  championBitches = 0
}) => {
  // Calculate Championship Points
  const championship = calculateChampionshipPoints({
    dogSchedule,
    bitchSchedule,
    classDogs,
    classBitches,
    championDogs,
    championBitches
  });
  
  // Calculate Grand Champion Points
  const grandChampion = calculateGrandChampionPoints({
    dogSchedule,
    bitchSchedule,
    classDogs,
    classBitches,
    championDogs,
    championBitches,
    championshipBOS: championship.BOS
  });
  
  return {
    championship,
    grandChampion,
    // Competition summary
    competition: {
      totalCompeting: classDogs + classBitches + championDogs + championBitches,
      totalDogs: classDogs + championDogs,
      totalBitches: classBitches + championBitches,
      classDogs,
      classBitches,
      championDogs,
      championBitches
    }
  };
};

/**
 * Validate calculation inputs
 * @param {Object} inputs - Input values to validate
 * @returns {Array} Array of validation error messages
 */
export const validateInputs = (inputs) => {
  const errors = [];
  const { selectedYear, selectedState, selectedBreed, classDogs, classBitches } = inputs;
  
  if (!selectedYear) errors.push('Please select a year');
  if (!selectedState) errors.push('Please select a state');
  if (!selectedBreed) errors.push('Please select a breed');
  if (classDogs === 0 && classBitches === 0) {
    errors.push('At least one class dog or bitch must compete');
  }
  
  return errors;
};

// Legacy exports for backward compatibility (if needed by other components)
export { getPoints as calculateChampionshipPoints };
export const calculateBOBPoints = (dogPoints, bitchPoints) => Math.max(dogPoints, bitchPoints);